import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const renderParagraphs = (paragraphs = []) =>
  paragraphs.map((paragraph, idx) => <p key={`${paragraph}-${idx}`}>{paragraph}</p>);

const renderList = (items = []) =>
  items.map((item, idx) => <li key={`${item}-${idx}`}>{item}</li>);

const renderCards = (items = [], className = "product-card") =>
  items.map((item, idx) => (
    <div
      key={item.title || item.label || idx}
      className={item.columnClass || (items.length === 4 ? "col-lg-3 col-md-6 mb-4" : "col-lg-4 col-md-6 mb-4")}
    >
      <article className={className}>
        <h3>{item.title || item.label}</h3>
        {item.text ? <p>{item.text}</p> : null}
        {item.items ? <ul className="product-bullets">{renderList(item.items)}</ul> : null}
        {item.link ? (
          <Link to={item.link.href} className="product-inline-link">
            {item.link.label}
          </Link>
        ) : null}
      </article>
    </div>
  ));

const SectionHeading = ({ eyebrow, title, lead }) => (
  <div className="product-section-heading">
    {eyebrow ? <p className="section-eyebrow">{eyebrow}</p> : null}
    {title ? <h2 className="section-title">{title}</h2> : null}
    {lead ? <p className="section-sub">{lead}</p> : null}
  </div>
);

const ProductHeritagePage = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  noindex = false,
  hero,
  overview,
  businessProblem,
  architecture,
  capabilities,
  challenges,
  scalability,
  integration,
  media,
  benefits,
  modernization,
  ai,
  relatedIndustries,
  relatedLegacyServices,
  why,
  cta,
}) => (
  <React.Fragment>
    <Helmet defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,follow" /> : null}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
    </Helmet>

    <main className="product-page">
      <section className="product-hero" aria-labelledby="product-hero-title">
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <span className="section-eyebrow">{hero.eyebrow}</span>
              <h1 id="product-hero-title">{hero.heading}</h1>
              <p className="product-hero-lead">{hero.lead}</p>
              <div className="product-hero-tags" aria-label={`${hero.heading} focus areas`}>
                {hero.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <div className="product-hero-actions">
                <Link to={hero.primaryCta.href} className="hero-v2-btn-primary">
                  {hero.primaryCta.label}
                </Link>
                <Link to={hero.secondaryCta.href} className="hero-v2-btn-outline">
                  {hero.secondaryCta.label}
                </Link>
                {hero.externalLinks
                  ? hero.externalLinks.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hero-v2-btn-outline"
                      >
                        {item.label}
                      </a>
                    ))
                  : null}
              </div>
            </div>

            <div
              className="product-hero-panel"
              style={{ "--product-hero-image": `url(${hero.image})` }}
              aria-hidden="true"
            >
              <div className="product-hero-panel-scrim" />
              <div className="product-hero-panel-inner">
                <p className="product-hero-panel-kicker">{hero.panelKicker}</p>
                <h2>{hero.panelTitle}</h2>
                <p>{hero.panelText}</p>
                {hero.stats ? (
                  <div className="product-hero-stats">
                    {hero.stats.map((stat) => (
                      <div key={stat.label} className="product-hero-stat">
                        <strong>{stat.value}</strong>
                        <span>{stat.label}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      {overview ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading eyebrow={overview.eyebrow} title={overview.title} lead={overview.lead} />
            <div className="row align-items-stretch">
              <div className="col-lg-7 mb-4">
                <div className="product-copy-card">{renderParagraphs(overview.paragraphs)}</div>
              </div>
              <div className="col-lg-5 mb-4">
                <div className="product-info-card product-info-card--accent">
                  <h3>{overview.callout.title}</h3>
                  <p>{overview.callout.text}</p>
                  {overview.callout.items ? (
                    <ul className="product-info-list">{renderList(overview.callout.items)}</ul>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {businessProblem ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading eyebrow={businessProblem.eyebrow} title={businessProblem.title} lead={businessProblem.lead} />
            <div className="row">{renderCards(businessProblem.items, "product-card")}</div>
          </div>
        </section>
      ) : null}

      {architecture ? (
        <section className="product-section">
          <div className="container">
            <div className="product-split">
              <div>
                <SectionHeading
                  eyebrow={architecture.eyebrow}
                  title={architecture.title}
                  lead={architecture.lead}
                />
                <div className="product-copy-card">{renderParagraphs(architecture.paragraphs)}</div>
              </div>
              <div className="product-info-card">
                <h3>{architecture.callout.title}</h3>
                {architecture.callout.items ? (
                  <ul className="product-info-list">{renderList(architecture.callout.items)}</ul>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {capabilities ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading eyebrow={capabilities.eyebrow} title={capabilities.title} lead={capabilities.lead} />
            <div className="row">
              {capabilities.items.map((item) => (
                <div key={item.title} className="col-lg-3 col-md-6 mb-4">
                  <article className="product-list-card">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                    {item.link ? (
                      <Link to={item.link.href} className="product-inline-link">
                        {item.link.label}
                      </Link>
                    ) : null}
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {challenges ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading eyebrow={challenges.eyebrow} title={challenges.title} lead={challenges.lead} />
            <div className="row">{renderCards(challenges.items, "product-card product-card--alt")}</div>
          </div>
        </section>
      ) : null}

      {scalability ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <div className="product-split">
              <div>
                <SectionHeading
                  eyebrow={scalability.eyebrow}
                  title={scalability.title}
                  lead={scalability.lead}
                />
                <div className="product-copy-card">{renderParagraphs(scalability.paragraphs)}</div>
              </div>
              <div className="product-info-card">
                <h3>{scalability.callout.title}</h3>
                <ul className="product-info-list">{renderList(scalability.callout.items)}</ul>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {integration ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading eyebrow={integration.eyebrow} title={integration.title} lead={integration.lead} />
            <div className="row align-items-stretch">
              <div className="col-lg-7 mb-4">
                <div className="product-copy-card">{renderParagraphs(integration.paragraphs)}</div>
              </div>
              <div className="col-lg-5 mb-4">
                <div className="product-info-card">
                  <h3>{integration.callout.title}</h3>
                  {integration.callout.items ? (
                    <ul className="product-info-list">{renderList(integration.callout.items)}</ul>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {media ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading eyebrow={media.eyebrow} title={media.title} lead={media.lead} />
            <div className="row">
              {media.items.map((item) => (
                <div key={item.src} className="col-lg-4 col-md-6 mb-4">
                  <article className="product-media-card">
                    <div className="product-media-frame">
                      <img src={item.src} alt={item.alt} />
                    </div>
                    {item.caption ? <h3>{item.caption}</h3> : null}
                    {item.text ? <p>{item.text}</p> : null}
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {benefits ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading eyebrow={benefits.eyebrow} title={benefits.title} lead={benefits.lead} />
            <div className="row">{renderCards(benefits.items, "product-card")}</div>
          </div>
        </section>
      ) : null}

      {modernization ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading eyebrow={modernization.eyebrow} title={modernization.title} lead={modernization.lead} />
            <div className="row align-items-stretch">
              <div className="col-lg-7 mb-4">
                <div className="product-copy-card">{renderParagraphs(modernization.paragraphs)}</div>
              </div>
              <div className="col-lg-5 mb-4">
                <div className="product-info-card product-info-card--accent">
                  <h3>{modernization.callout.title}</h3>
                  <ul className="product-info-list">
                    {renderList(modernization.callout.items)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {ai ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading eyebrow={ai.eyebrow} title={ai.title} lead={ai.lead} />
            <div className="row">{renderCards(ai.items, "product-card product-card--ai")}</div>
            <div className="text-center mt-2">
              <Link to="/enterprise-ai" className="product-inline-link">
                Explore Enterprise AI
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      {relatedIndustries ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading
              eyebrow={relatedIndustries.eyebrow}
              title={relatedIndustries.title}
              lead={relatedIndustries.lead}
            />
            <div className="row">
              {relatedIndustries.items.map((item) => (
                <div key={item.href} className="col-lg-3 col-md-6 mb-4">
                  <Link to={item.href} className="product-link-card">
                    <h3>{item.label}</h3>
                    <p>{item.text}</p>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {relatedLegacyServices ? (
        <section className="product-section product-section--muted">
          <div className="container">
            <SectionHeading
              eyebrow={relatedLegacyServices.eyebrow}
              title={relatedLegacyServices.title}
              lead={relatedLegacyServices.lead}
            />
            <div className="row">
              {relatedLegacyServices.items.map((item) => (
                <div key={item.href} className="col-lg-3 col-md-6 mb-4">
                  <Link to={item.href} className="product-link-card">
                    <h3>{item.label}</h3>
                    <p>{item.text}</p>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {why ? (
        <section className="product-section">
          <div className="container">
            <SectionHeading eyebrow={why.eyebrow} title={why.title} lead={why.lead} />
            <div className="row">{renderCards(why.items, "product-card")}</div>
          </div>
        </section>
      ) : null}

      {cta ? (
        <section className="product-cta">
          <div className="container">
            <div className="product-cta-panel">
              <div>
                <p className="section-eyebrow">{cta.eyebrow}</p>
                <h2>{cta.title}</h2>
                <p>{cta.text}</p>
              </div>
              <div className="product-cta-actions">
                <Link to={cta.primary.href} className="hero-v2-btn-primary">
                  {cta.primary.label}
                </Link>
                <Link to={cta.secondary.href} className="hero-v2-btn-outline">
                  {cta.secondary.label}
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </main>
  </React.Fragment>
);

export default ProductHeritagePage;
